
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Check, ChevronDown, ChevronUp } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ConversationalThemeSelectionProps {
  onOptionsChange: (options: {
    selectedThemes: string[];
    includeVerse: boolean;
    bilingual: boolean;
    subject?: string;
  }) => void;
  disabled?: boolean;
}

const POPULAR_THEMES = [
  { value: 'gratitude', label: 'Gratitude', icon: '🙏' },
  { value: 'healing', label: 'Healing', icon: '💚' },
  { value: 'peace', label: 'Peace', icon: '☮️' },
  { value: 'strength', label: 'Strength', icon: '💪' }
];

const MORE_THEMES = [
  { value: 'forgiveness', label: 'Forgiveness', icon: '🕊️' },
  { value: 'guidance', label: 'Guidance', icon: '🧭' },
  { value: 'protection', label: 'Protection', icon: '🛡️' },
  { value: 'faith', label: 'Faith', icon: '✨' },
  { value: 'family', label: 'Family', icon: '👨‍👩‍👧‍👦' },
  { value: 'hope', label: 'Hope', icon: '🌅' }
];

export default function ConversationalThemeSelection({ 
  onOptionsChange, 
  disabled = false 
}: ConversationalThemeSelectionProps) {
  const [selectedThemes, setSelectedThemes] = useState<string[]>([]);
  const [showMoreThemes, setShowMoreThemes] = useState(false);
  const [includeVerse, setIncludeVerse] = useState(false);
  const [bilingual, setBilingual] = useState(false);
  const [subject, setSubject] = useState('');

  const handleThemeToggle = (themeValue: string) => {
    const newSelectedThemes = selectedThemes.includes(themeValue)
      ? selectedThemes.filter(t => t !== themeValue)
      : [...selectedThemes, themeValue];
    
    setSelectedThemes(newSelectedThemes);
    onOptionsChange({
      selectedThemes: newSelectedThemes,
      includeVerse,
      bilingual,
      subject: subject || undefined
    });
  };

  const handleIncludeVerseChange = (checked: boolean) => {
    setIncludeVerse(checked);
    onOptionsChange({
      selectedThemes,
      includeVerse: checked,
      bilingual,
      subject: subject || undefined
    });
  };

  const handleBilingualChange = (checked: boolean) => {
    setBilingual(checked);
    onOptionsChange({
      selectedThemes,
      includeVerse,
      bilingual: checked,
      subject: subject || undefined
    });
  };

  const handleSubjectChange = (value: string) => {
    setSubject(value);
    onOptionsChange({
      selectedThemes,
      includeVerse,
      bilingual,
      subject: value || undefined
    });
  };

  const ThemeBubble = ({ theme, selected, onClick }: { 
    theme: { value: string; label: string; icon: string }; 
    selected: boolean; 
    onClick: () => void;
  }) => (
    <Button
      type="button"
      variant={selected ? "default" : "outline"}
      size="sm"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex items-center gap-2 h-8 md:h-9 px-2.5 md:px-3 rounded-full transition-all",
        "focus:ring-2 focus:ring-primary/20 focus:outline-none",
        selected && "bg-primary text-primary-foreground shadow-sm"
      )}
      aria-label={`Theme: ${theme.label}, ${selected ? 'selected' : 'not selected'}`}
      aria-pressed={selected}
    >
      <span className="text-sm">{theme.icon}</span>
      <span className="text-sm font-medium">{theme.label}</span>
      {selected && <Check className="h-3 w-3 ml-1" />}
    </Button>
  );

  return (
    <Card className="p-3 space-y-3 md:p-4 md:space-y-4 bg-card/50 backdrop-blur-sm border-muted">
      {/* Theme Selection */}
      <div className="space-y-2 md:space-y-3">
        {/* Popular Themes */}
        <div className="flex flex-wrap gap-2 md:gap-2 justify-center">
          {POPULAR_THEMES.map((theme) => (
            <ThemeBubble
              key={theme.value}
              theme={theme}
              selected={selectedThemes.includes(theme.value)}
              onClick={() => handleThemeToggle(theme.value)}
            />
          ))}
        </div>

        {/* Show More Toggle */}
        <div className="text-center">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowMoreThemes(!showMoreThemes)}
            disabled={disabled}
            className="text-muted-foreground hover:text-foreground"
          >
            {showMoreThemes ? (
              <>
                <ChevronUp className="h-4 w-4 mr-1" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="h-4 w-4 mr-1" />
                Show more topics
              </>
            )}
          </Button>
        </div>

        {/* More Themes */}
        {showMoreThemes && (
          <div className="flex flex-wrap gap-2 justify-center animate-fade-in">
            {MORE_THEMES.map((theme) => (
              <ThemeBubble
                key={theme.value}
                theme={theme}
                selected={selectedThemes.includes(theme.value)}
                onClick={() => handleThemeToggle(theme.value)}
              />
            ))}
          </div>
        )}

      </div>

      {/* Prayer Options */}
      <div className="space-y-2 md:space-y-3 border-t border-muted pt-3 md:pt-4">
        <div className="flex flex-col sm:flex-row gap-3 md:gap-4 justify-center">
          <div className="flex items-center space-x-1.5 md:space-x-2">
            <Switch
              id="include-verse"
              checked={includeVerse}
              onCheckedChange={handleIncludeVerseChange}
              disabled={disabled}
            />
            <Label htmlFor="include-verse" className="text-sm">Include Bible verse</Label>
          </div>
          
          <div className="flex items-center space-x-1.5 md:space-x-2">
            <Switch
              id="bilingual"
              checked={bilingual}
              onCheckedChange={handleBilingualChange}
              disabled={disabled}
            />
            <Label htmlFor="bilingual" className="text-sm">Bilingual prayer</Label>
          </div>
        </div>

        <div className="space-y-1.5 md:space-y-2">
          <Label htmlFor="subject" className="text-sm text-muted-foreground">
            Prayer subject (optional)
          </Label>
          <Input
            id="subject"
            value={subject}
            onChange={(e) => handleSubjectChange(e.target.value)}
            placeholder="e.g., For my family, For healing..."
            disabled={disabled}
            className="text-center"
          />
        </div>
      </div>

      {/* Selection Summary */}
      {selectedThemes.length > 0 && (
        <div className="text-center text-sm text-muted-foreground border-t border-muted pt-2 md:pt-3">
          You've selected: {selectedThemes.join(', ')}
        </div>
      )}
    </Card>
  );
}
